import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { createPortal } from 'react-dom';
import { db } from '@/lib/firebase';
import { doc, updateDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useTenant } from '@/contexts/TenantContext';
import { 
    DEFAULT_OBJECT_PERMISSIONS, 
    DEFAULT_PROPERTY_PERMISSIONS, 
    ROLE_PERMISSION_PRESETS 
} from '@/hooks/usePermissions';

// Object schema metadata with categories and descriptive field lists
const OBJECT_SCHEMAS = {
    deals: {
        title: 'Deals',
        desc: 'Store, track, and manage the revenue opportunities your team is working on.',
        category: 'crm_objects',
        icon: 'monetization_on',
        properties: [
            { key: 'amount', label: 'Deal Amount / Revenue', group: 'Financial & Deal Metrics', desc: 'Total contract value or revenue figure for this deal.' },
            { key: 'gross_margin', label: 'Gross Profit Margin %', group: 'Financial & Deal Metrics', desc: 'Calculated profit margin after cost of sales (highly sensitive).' },
            { key: 'commission_rate', label: 'Commission Rate / Payout', group: 'Financial & Deal Metrics', desc: 'Rep commission percentage and projected payout.' },
            { key: 'discount_pct', label: 'Discount %', group: 'Financial & Deal Metrics', desc: 'Negotiated discount percentage granted to the client.' },
            { key: 'payment_terms', label: 'Payment & Net Terms', group: 'Financial & Deal Metrics', desc: 'Net 30/60/90, milestone split, or upfront payment structure.' },
            { key: 'stage', label: 'Deal Stage', group: 'Pipeline & Routing', desc: 'Current pipeline stage (e.g. Discovery, Proposal, Closed Won).' },
            { key: 'deal_owner', label: 'Assigned Deal Owner', group: 'Pipeline & Routing', desc: 'Sales rep or executive managing this opportunity.' },
            { key: 'closedate', label: 'Target Close Date', group: 'Pipeline & Routing', desc: 'Projected closing or signature date.' },
            { key: 'probability', label: 'Win Probability %', group: 'Pipeline & Routing', desc: 'Estimated closing likelihood percentage.' },
            { key: 'billing_contact', label: 'Billing & Finance Contact', group: 'Account & Contact Data', desc: 'Client accounts payable contact details.' }
        ]
    },
    leads: {
        title: 'Leads',
        desc: 'Store, track and manage the inbound prospects and leads your team is working on.',
        category: 'crm_objects',
        icon: 'filter_list',
        properties: [
            { key: 'phone', label: 'Direct Phone / Mobile', group: 'Contact Details', desc: 'Primary dialer number for the prospect.' },
            { key: 'email', label: 'Primary Email Address', group: 'Contact Details', desc: 'Work or personal email address.' },
            { key: 'annual_revenue', label: 'Estimated Annual Revenue', group: 'Financial & Qualification', desc: 'Prospect company turnover or purchasing budget.' },
            { key: 'lead_source', label: 'Lead Source / Campaign', group: 'Pipeline & Attribution', desc: 'Acquisition channel (e.g. Inbound Web, Summit, Cold Call).' },
            { key: 'notes', label: 'Sales Notes & Background', group: 'Pipeline & Attribution', desc: 'Qualification notes logged by agents.' },
            { key: 'credit_score', label: 'Client Credit Rating', group: 'Financial & Qualification', desc: 'Financial solvency assessment score.' }
        ]
    },
    contacts: {
        title: 'Contacts',
        desc: 'Save important info about your customers as a contact, so your team can connect with them.',
        category: 'crm_objects',
        icon: 'contacts',
        properties: [
            { key: 'phone', label: 'Primary Phone', group: 'Contact Details', desc: 'Main telephone number.' },
            { key: 'email', label: 'Email Address', group: 'Contact Details', desc: 'Direct corporate email.' },
            { key: 'direct_line', label: 'Direct Desk Extension', group: 'Contact Details', desc: 'Direct desk line or mobile.' },
            { key: 'personal_notes', label: 'Private Relationship Notes', group: 'Account Data', desc: 'Notes on personal preferences, VIP status, or history.' }
        ]
    },
    companies: {
        title: 'Companies',
        desc: 'Save useful info about organizations and target accounts in your database.',
        category: 'crm_objects',
        icon: 'corporate_fare',
        properties: [
            { key: 'annual_revenue', label: 'Company Annual Revenue', group: 'Financial & Scale', desc: 'Reported corporate annual revenue.' },
            { key: 'employee_count', label: 'Total Employee Count', group: 'Firmographics', desc: 'Estimated organization size.' },
            { key: 'tax_id', label: 'Tax ID / EIN Number', group: 'Financial & Scale', desc: 'Confidential corporate tax registration number.' }
        ]
    },
    call_audits: {
        title: 'Call Audits',
        desc: 'Review call recordings, transcripts, AI rubric scores, and agent QA metrics.',
        category: 'qms_audits',
        icon: 'record_voice_over',
        properties: [
            { key: 'score', label: 'AI QA Overall Score', group: 'Quality Scoring', desc: 'Overall quality score evaluated by AI rubrics.' },
            { key: 'transcript', label: 'Full Audio Transcript', group: 'Media & Transcripts', desc: 'Complete transcribed conversation text.' },
            { key: 'audio_url', label: 'Call Audio Stream URL', group: 'Media & Transcripts', desc: 'Direct link to stream audio recording.' },
            { key: 'coaching_notes', label: 'Manager Coaching Notes', group: 'Quality Scoring', desc: 'Confidential feedback memo from QA supervisor.' },
            { key: 'dispute_status', label: 'Agent Dispute Status', group: 'Quality Scoring', desc: 'Status of rep dispute filing or grievance.' }
        ]
    },
    deal_audits: {
        title: 'Deal Lifecycle Audits',
        desc: 'Manage and review deal lifecycle quality assurance, stage progression, and compliance.',
        category: 'qms_audits',
        icon: 'verified',
        properties: [
            { key: 'compliance_status', label: 'Contract Compliance Status', group: 'Audit Metrics', desc: 'Verification of KYC, signature, and payment receipt.' },
            { key: 'auditor_notes', label: 'Auditor Evaluation Memo', group: 'Audit Metrics', desc: 'Detailed notes recorded by compliance auditor.' }
        ]
    },
    escalations: {
        title: 'Escalation Cases',
        desc: 'Manage client escalations, dispute resolutions, and operational interventions.',
        category: 'qms_cases',
        icon: 'warning',
        properties: [
            { key: 'severity', label: 'Escalation Severity Level', group: 'Case Management', desc: 'P1 (Critical), P2 (High), or P3 (Standard).' },
            { key: 'resolution_notes', label: 'Executive Resolution Plan', group: 'Case Management', desc: 'Steps taken to resolve customer dispute.' },
            { key: 'internal_memo', label: 'Internal Executive Memo', group: 'Case Management', desc: 'Restricted memo not visible to clients or general reps.' }
        ]
    }
};

const CATEGORIES = [
    {
        id: 'crm',
        label: 'CRM',
        subcategories: [
            { id: 'crm_objects', label: 'CRM objects' },
            { id: 'crm_tools', label: 'CRM tools' }
        ]
    },
    {
        id: 'qms',
        label: 'Quality (QMS)',
        subcategories: [
            { id: 'qms_audits', label: 'Audits' },
            { id: 'qms_cases', label: 'Escalations' }
        ]
    },
    {
        id: 'lms',
        label: 'Academy (LMS)',
        subcategories: [
            { id: 'lms_courses', label: 'Courses' }
        ]
    },
    {
        id: 'account',
        label: 'Account',
        subcategories: [
            { id: 'account_settings', label: 'Settings access' }
        ]
    }
];

export default function UserPermissionsEditor({ user, onClose, onSaveSuccess }) {
    const { currentTenant } = useTenant();
    const [mainTab, setMainTab] = useState('access'); // 'access' | 'review'
    const [activeSubCategory, setActiveSubCategory] = useState('crm_objects');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Property Drawer state
    const [activePropertyObjectKey, setActivePropertyObjectKey] = useState(null);
    const [propertySearchQuery, setPropertySearchQuery] = useState('');

    // Core Permissions State
    const [objectPermissions, setObjectPermissions] = useState(() => {
        return user?.permissions?.objects || DEFAULT_OBJECT_PERMISSIONS;
    });

    const [propertyPermissions, setPropertyPermissions] = useState(() => {
        return user?.permissions?.properties || DEFAULT_PROPERTY_PERMISSIONS;
    });

    const [saving, setSaving] = useState(false);
    const [saveNotification, setSaveNotification] = useState(null);

    // Filter objects for the active subcategory
    const currentCategoryObjects = useMemo(() => {
        return Object.entries(OBJECT_SCHEMAS)
            .filter(([key, data]) => data.category === activeSubCategory)
            .filter(([key, data]) => {
                if (!searchQuery) return true;
                const q = searchQuery.toLowerCase();
                return data.title.toLowerCase().includes(q) || data.desc.toLowerCase().includes(q);
            });
    }, [activeSubCategory, searchQuery]);

    // Handle Object-Level Scope Change
    const handleObjectScopeChange = (objectKey, field, value) => {
        setObjectPermissions(prev => ({
            ...prev,
            [objectKey]: {
                ...(prev[objectKey] || DEFAULT_OBJECT_PERMISSIONS[objectKey] || { enabled: true }),
                [field]: value
            }
        }));
    };

    // Toggle master object ON / OFF
    const toggleObjectEnabled = (objectKey) => {
        setObjectPermissions(prev => {
            const current = prev[objectKey]?.enabled ?? true;
            return {
                ...prev,
                [objectKey]: {
                    ...(prev[objectKey] || DEFAULT_OBJECT_PERMISSIONS[objectKey] || {}),
                    enabled: !current
                }
            };
        });
    };

    // Handle Property-Level Access Change
    const handlePropertyAccessChange = (objectKey, propKey, tier) => {
        setPropertyPermissions(prev => ({
            ...prev,
            [objectKey]: {
                ...(prev[objectKey] || {}),
                [propKey]: tier
            }
        }));
    };

    // Apply 1-Click Role Preset
    const applyPreset = (presetKey) => {
        const preset = ROLE_PERMISSION_PRESETS[presetKey];
        if (!preset) return;

        setObjectPermissions(prev => ({
            ...prev,
            ...preset.objects
        }));

        setPropertyPermissions(prev => ({
            ...prev,
            ...preset.properties
        }));

        setSaveNotification(`Applied '${preset.name}' preset template.`);
        setTimeout(() => setSaveNotification(null), 3000);
    };

    // Save to Firestore
    const handleSave = async () => {
        if (!user?.id) return;
        setSaving(true);
        try {
            const payload = {
                permissions: {
                    objects: objectPermissions,
                    properties: propertyPermissions,
                    updatedAt: serverTimestamp(),
                    updatedBy: 'Admin'
                }
            };

            // 1. Update app_users
            const userRef = doc(db, 'app_users', user.id);
            await updateDoc(userRef, payload);

            // 2. Also dual-sync to tenant_users if tenant is active
            if (currentTenant?.id && user.email) {
                const tuId = `${currentTenant.id}_${user.email.toLowerCase()}`;
                try {
                    await updateDoc(doc(db, 'tenant_users', tuId), {
                        permissions: payload.permissions
                    });
                } catch (tuErr) {
                    // Fallback to setDoc with merge if doc does not exist yet
                    await setDoc(doc(db, 'tenant_users', tuId), {
                        tenantId: currentTenant.id,
                        email: user.email.toLowerCase(),
                        role: user.role || 'user',
                        permissions: payload.permissions
                    }, { merge: true });
                }
            }

            setSaveNotification('Permissions saved successfully!');
            if (onSaveSuccess) onSaveSuccess({ ...user, permissions: payload.permissions });
            setTimeout(() => {
                setSaveNotification(null);
                onClose();
            }, 900);
        } catch (err) {
            console.error('Failed to save permissions:', err);
            setSaveNotification('Failed to save permissions: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const activePropertyObject = activePropertyObjectKey ? OBJECT_SCHEMAS[activePropertyObjectKey] : null;

    // Filter properties for slide-over drawer
    const filteredDrawerProperties = useMemo(() => {
        if (!activePropertyObject) return [];
        return activePropertyObject.properties.filter(p => {
            if (!propertySearchQuery) return true;
            const q = propertySearchQuery.toLowerCase();
            return p.label.toLowerCase().includes(q) || p.key.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q);
        });
    }, [activePropertyObject, propertySearchQuery]);

    // Group drawer properties by category
    const groupedDrawerProperties = useMemo(() => {
        const groups = {};
        filteredDrawerProperties.forEach(p => {
            const g = p.group || 'General Properties';
            if (!groups[g]) groups[g] = [];
            groups[g].push(p);
        });
        return groups;
    }, [filteredDrawerProperties]);

    const modalContent = (
        <div className="fixed inset-0 z-[10000] bg-[var(--bg-page)] flex flex-col animate-fade-in font-sans">
            {/* Top Navigation Bar */}
            <div className="h-14 bg-[#1e293b] px-6 flex items-center justify-between text-white border-b border-slate-800">
                <button 
                    onClick={onClose} 
                    className="bg-transparent border-0 text-slate-300 hover:text-white flex items-center gap-2 cursor-pointer text-xs font-semibold uppercase tracking-wider transition-colors"
                >
                    <ObliqueIcon name="chevron_left" size={18} /> Back to Users
                </button>
                
                <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold tracking-tight">Editing Access & Field Security:</span>
                    <span className="px-2.5 py-1 bg-white/10 rounded-full text-xs font-bold text-white border border-white/20">
                        {user?.primaryName || user?.name || user?.email || 'User'}
                    </span>
                    {user?.role && (
                        <span className="text-xs text-slate-400 capitalize">({user.role})</span>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <button 
                        onClick={onClose}
                        className="px-3 py-1.5 rounded bg-transparent text-slate-300 hover:text-white text-xs font-medium cursor-pointer border border-slate-700 transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSave} 
                        disabled={saving}
                        className="bg-white text-slate-900 hover:bg-slate-100 border-0 px-5 py-1.5 rounded text-xs font-bold cursor-pointer shadow-md flex items-center gap-1.5 transition-all"
                    >
                        {saving ? (
                            <>
                                <ObliqueIcon name="sync" size={14} className="animate-spin" /> Saving...
                            </>
                        ) : (
                            <>
                                <ObliqueIcon name="check" size={14} /> Save Permissions
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Notification Toast Banner */}
            <AnimatePresence>
                {saveNotification && (
                    <motion.div 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="absolute top-16 left-1/2 -translate-x-1/2 z-[10050] bg-slate-900 text-white px-5 py-2.5 rounded-full shadow-2xl border border-slate-700 text-xs font-semibold flex items-center gap-2 backdrop-blur-md"
                    >
                        <ObliqueIcon name="verified" size={16} className="text-emerald-400" />
                        {saveNotification}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Sub-Header / Mode Tabs & Preset Bar */}
            <div className="h-12 border-b border-[var(--border)] bg-[var(--bg-surface)] px-8 flex items-center justify-between">
                <div className="flex gap-8 h-full">
                    <button 
                        onClick={() => setMainTab('access')}
                        className={`h-full flex items-center gap-2 text-xs font-bold border-b-2 cursor-pointer transition-all ${
                            mainTab === 'access' 
                                ? 'border-[var(--text-primary)] text-[var(--text-primary)]' 
                                : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                        }`}
                    >
                        <ObliqueIcon name="tune" size={16} /> Access & Objects
                    </button>
                    <button 
                        onClick={() => setMainTab('review')}
                        className={`h-full flex items-center gap-2 text-xs font-bold border-b-2 cursor-pointer transition-all ${
                            mainTab === 'review' 
                                ? 'border-[var(--text-primary)] text-[var(--text-primary)]' 
                                : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                        }`}
                    >
                        <ObliqueIcon name="rate_review" size={16} /> Review Effective Policy
                    </button>
                </div>

                {/* 1-Click Quick Presets */}
                <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">Presets:</span>
                    <button 
                        onClick={() => applyPreset('sales_rep')}
                        className="px-2.5 py-1 text-[11px] font-semibold rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 cursor-pointer transition-colors"
                        title="Owned deals only, margins/commissions restricted, export blocked"
                    >
                        Sales Rep
                    </button>
                    <button 
                        onClick={() => applyPreset('sdr_inbound')}
                        className="px-2.5 py-1 text-[11px] font-semibold rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-pointer transition-colors"
                        title="View/Edit leads, financial metrics locked"
                    >
                        SDR Inbound
                    </button>
                    <button 
                        onClick={() => applyPreset('qa_auditor')}
                        className="px-2.5 py-1 text-[11px] font-semibold rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 cursor-pointer transition-colors"
                        title="View-only on pipeline, full QA score editing"
                    >
                        QA Auditor
                    </button>
                    <button 
                        onClick={() => applyPreset('superadmin')}
                        className="px-2.5 py-1 text-[11px] font-semibold rounded bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 cursor-pointer transition-colors"
                        title="Full unrestricted access across all objects and fields"
                    >
                        SuperAdmin
                    </button>
                </div>
            </div>

            {/* Tab 1: Access & Configuration Matrix */}
            {mainTab === 'access' && (
                <div className="flex-1 flex overflow-hidden">
                    {/* Left Category Sidebar */}
                    <div className="w-64 border-r border-[var(--border)] bg-[var(--bg-surface)] overflow-y-auto py-5 custom-scrollbar">
                        <div className="px-5 mb-4">
                            <div className="flex items-center gap-2 bg-[var(--bg-muted)] border border-[var(--border)] rounded px-3 py-1.5">
                                <ObliqueIcon name="search" size={14} color="var(--text-tertiary)" />
                                <input 
                                    placeholder="Search objects..." 
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="border-none bg-transparent outline-none text-xs w-full text-[var(--text-primary)]" 
                                />
                            </div>
                        </div>

                        {CATEGORIES.map(category => (
                            <div key={category.id} className="mb-4">
                                <div className="px-5 py-1.5 text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider flex items-center justify-between">
                                    {category.label}
                                </div>
                                <div className="flex flex-col mt-1">
                                    {category.subcategories.map(sub => {
                                        const isActive = activeSubCategory === sub.id;
                                        return (
                                            <div 
                                                key={sub.id} 
                                                onClick={() => setActiveSubCategory(sub.id)}
                                                className={`px-5 py-2 text-xs font-semibold cursor-pointer transition-all flex items-center justify-between ${
                                                    isActive 
                                                        ? 'bg-[var(--bg-muted)] text-[var(--text-primary)] border-l-3 border-[var(--text-primary)]' 
                                                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)] border-l-3 border-transparent'
                                                }`}
                                            >
                                                <span>{sub.label}</span>
                                                <ObliqueIcon name="chevron_right" size={14} color={isActive ? 'var(--text-primary)' : 'var(--text-tertiary)'} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        <div className="p-5 mt-6 border-t border-[var(--border)]">
                            <div className="text-[11px] text-[var(--text-tertiary)] leading-relaxed">
                                <strong>HubSpot-Grade RBAC</strong>: Changes made here take effect immediately upon saving and apply across all active sessions.
                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 bg-[var(--bg-page)] overflow-y-auto p-8 custom-scrollbar">
                        <div className="max-w-4xl mx-auto">
                            {/* Section Header */}
                            <div className="mb-6 flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-bold text-[var(--text-primary)] capitalize">
                                        {activeSubCategory.replace('_', ' ')}
                                    </h2>
                                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                                        Configure object-level permissions (View, Edit, Delete, Create, Export) and granular field-level security.
                                    </p>
                                </div>
                                <div className="text-xs font-medium text-[var(--text-tertiary)]">
                                    {currentCategoryObjects.length} Object{currentCategoryObjects.length === 1 ? '' : 's'} Configurable
                                </div>
                            </div>

                            {/* Object Cards List */}
                            {currentCategoryObjects.length === 0 ? (
                                <div className="p-12 text-center text-xs text-[var(--text-tertiary)] bg-[var(--bg-surface)] rounded-xl border border-[var(--border)]">
                                    No objects match your current filter. Select another category on the left.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {currentCategoryObjects.map(([objectKey, objData]) => {
                                        const perm = objectPermissions[objectKey] || DEFAULT_OBJECT_PERMISSIONS[objectKey] || { enabled: true };
                                        const isEnabled = perm.enabled !== false;
                                        const hasProps = objData.properties && objData.properties.length > 0;
                                        
                                        // Count restricted properties for this object
                                        const objPropPerms = propertyPermissions[objectKey] || {};
                                        const restrictedCount = Object.values(objPropPerms).filter(v => v === 'restricted').length;

                                        return (
                                            <div 
                                                key={objectKey}
                                                className={`bg-[var(--bg-surface)] border rounded-xl overflow-hidden shadow-xs transition-all ${
                                                    isEnabled ? 'border-[var(--border)]' : 'border-[var(--border)] opacity-60'
                                                }`}
                                            >
                                                {/* Object Header Bar */}
                                                <div className="p-5 border-b border-[var(--border)] flex items-start justify-between bg-[var(--bg-surface)]">
                                                    <div className="flex items-start gap-3.5">
                                                        <div className="w-9 h-9 rounded-lg bg-[var(--bg-muted)] border border-[var(--border)] flex items-center justify-center text-[var(--text-primary)] mt-0.5">
                                                            <ObliqueIcon name={objData.icon || 'folder'} size={20} />
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <h3 className="text-sm font-bold text-[var(--text-primary)]">{objData.title}</h3>
                                                                {restrictedCount > 0 && (
                                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                                                        {restrictedCount} Property Restricted
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-[var(--text-secondary)] mt-0.5 max-w-xl">
                                                                {objData.desc}
                                                            </p>
                                                            {hasProps && isEnabled && (
                                                                <button 
                                                                    onClick={() => setActivePropertyObjectKey(objectKey)}
                                                                    className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0"
                                                                >
                                                                    <ObliqueIcon name="lock" size={13} />
                                                                    Manage field-level property access ({objData.properties.length} fields)
                                                                    <ObliqueIcon name="open_in_new" size={12} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Master Toggle Pill */}
                                                    <div className="flex items-center gap-2">
                                                        <div 
                                                            onClick={() => toggleObjectEnabled(objectKey)}
                                                            className={`cursor-pointer px-3 py-1 rounded text-xs font-bold transition-all flex items-center gap-1.5 ${
                                                                isEnabled 
                                                                    ? 'bg-[var(--text-primary)] text-[var(--bg-page)]' 
                                                                    : 'bg-[var(--bg-muted)] text-[var(--text-tertiary)] border border-[var(--border)]'
                                                            }`}
                                                        >
                                                            <ObliqueIcon name={isEnabled ? 'check' : 'close'} size={14} />
                                                            {isEnabled ? 'ENABLED' : 'DISABLED'}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Object-Level Scopes Matrix */}
                                                {isEnabled && (
                                                    <div className="p-5 bg-[var(--bg-subtle)]/40 grid grid-cols-2 md:grid-cols-5 gap-4">
                                                        {/* View Scope */}
                                                        <div>
                                                            <label className="block text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">
                                                                View Access
                                                            </label>
                                                            <select
                                                                value={perm.view || 'everything'}
                                                                onChange={(e) => handleObjectScopeChange(objectKey, 'view', e.target.value)}
                                                                className="w-full text-xs font-semibold py-1.5 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] outline-none cursor-pointer"
                                                            >
                                                                <option value="everything">Everything</option>
                                                                <option value="team_only">Team only</option>
                                                                <option value="owned_only">Owned only</option>
                                                                <option value="none">None</option>
                                                            </select>
                                                        </div>

                                                        {/* Edit Scope */}
                                                        <div>
                                                            <label className="block text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">
                                                                Edit Access
                                                            </label>
                                                            <select
                                                                value={perm.edit || 'everything'}
                                                                onChange={(e) => handleObjectScopeChange(objectKey, 'edit', e.target.value)}
                                                                className="w-full text-xs font-semibold py-1.5 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] outline-none cursor-pointer"
                                                            >
                                                                <option value="everything">Everything</option>
                                                                <option value="team_only">Team only</option>
                                                                <option value="owned_only">Owned only</option>
                                                                <option value="none">None</option>
                                                            </select>
                                                        </div>

                                                        {/* Delete Scope */}
                                                        <div>
                                                            <label className="block text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">
                                                                Delete Access
                                                            </label>
                                                            <select
                                                                value={perm.delete || 'none'}
                                                                onChange={(e) => handleObjectScopeChange(objectKey, 'delete', e.target.value)}
                                                                className="w-full text-xs font-semibold py-1.5 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] outline-none cursor-pointer"
                                                            >
                                                                <option value="everything">Everything</option>
                                                                <option value="team_only">Team only</option>
                                                                <option value="owned_only">Owned only</option>
                                                                <option value="none">None</option>
                                                            </select>
                                                        </div>

                                                        {/* Create Toggle */}
                                                        <div>
                                                            <label className="block text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">
                                                                Create Records
                                                            </label>
                                                            <select
                                                                value={perm.create ? 'allowed' : 'restricted'}
                                                                onChange={(e) => handleObjectScopeChange(objectKey, 'create', e.target.value === 'allowed')}
                                                                className="w-full text-xs font-semibold py-1.5 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] outline-none cursor-pointer"
                                                            >
                                                                <option value="allowed">Allowed</option>
                                                                <option value="restricted">Restricted</option>
                                                            </select>
                                                        </div>

                                                        {/* Export Anti-Leak Control */}
                                                        <div>
                                                            <label className="block text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider mb-1">
                                                                Export CSV / DB
                                                            </label>
                                                            <select
                                                                value={perm.export ? 'allowed' : 'restricted'}
                                                                onChange={(e) => handleObjectScopeChange(objectKey, 'export', e.target.value === 'allowed')}
                                                                className="w-full text-xs font-semibold py-1.5 px-2 rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-primary)] outline-none cursor-pointer"
                                                            >
                                                                <option value="allowed">Allowed</option>
                                                                <option value="restricted">Restricted</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Tab 2: Review Effective Policy */}
            {mainTab === 'review' && (
                <div className="flex-1 bg-[var(--bg-page)] overflow-y-auto p-10 custom-scrollbar">
                    <div className="max-w-4xl mx-auto bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden shadow-xs">
                        <div className="p-6 border-b border-[var(--border)] bg-[var(--bg-surface)]">
                            <h3 className="text-base font-bold text-[var(--text-primary)]">Effective Permission Matrix</h3>
                            <p className="text-xs text-[var(--text-secondary)] mt-1">
                                Review the complete set of object scopes and restricted properties configured for <strong>{user?.primaryName || user?.email}</strong>.
                            </p>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead>
                                    <tr className="border-b border-[var(--border)] bg-[var(--bg-subtle)] text-[11px] font-bold text-[var(--text-tertiary)] uppercase tracking-wider">
                                        <th className="py-3 px-6">Object</th>
                                        <th className="py-3 px-4">Status</th>
                                        <th className="py-3 px-4">View</th>
                                        <th className="py-3 px-4">Edit</th>
                                        <th className="py-3 px-4">Delete</th>
                                        <th className="py-3 px-4">Create</th>
                                        <th className="py-3 px-4">Export</th>
                                        <th className="py-3 px-6">Restricted Properties</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(OBJECT_SCHEMAS).map(([key, data]) => {
                                        const perm = objectPermissions[key] || DEFAULT_OBJECT_PERMISSIONS[key] || { enabled: true };
                                        const isEnabled = perm.enabled !== false;
                                        const objProps = propertyPermissions[key] || {};
                                        const restrictedKeys = Object.entries(objProps)
                                            .filter(([k, v]) => v === 'restricted')
                                            .map(([k]) => k);

                                        return (
                                            <tr key={key} className="border-b border-[var(--border)] hover:bg-[var(--bg-muted)]/50">
                                                <td className="py-3.5 px-6 font-bold text-[var(--text-primary)] flex items-center gap-2">
                                                    <ObliqueIcon name={data.icon || 'folder'} size={16} />
                                                    {data.title}
                                                </td>
                                                <td className="py-3.5 px-4">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                                        isEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                                                    }`}>
                                                        {isEnabled ? 'Active' : 'Disabled'}
                                                    </span>
                                                </td>
                                                <td className="py-3.5 px-4 capitalize font-medium">{perm.view || 'everything'}</td>
                                                <td className="py-3.5 px-4 capitalize font-medium">{perm.edit || 'everything'}</td>
                                                <td className="py-3.5 px-4 capitalize font-medium">{perm.delete || 'none'}</td>
                                                <td className="py-3.5 px-4">
                                                    {perm.create ? <span className="text-emerald-600 font-semibold">Yes</span> : <span className="text-slate-400">No</span>}
                                                </td>
                                                <td className="py-3.5 px-4">
                                                    {perm.export ? <span className="text-emerald-600 font-semibold">Yes</span> : <span className="text-amber-600 font-semibold">Blocked</span>}
                                                </td>
                                                <td className="py-3.5 px-6">
                                                    {restrictedKeys.length > 0 ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            {restrictedKeys.map(rk => (
                                                                <span key={rk} className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-mono font-medium">
                                                                    {rk}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-400 italic">None (All visible)</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Slide-Over: Field-Level Property Access Studio */}
            <AnimatePresence>
                {activePropertyObjectKey && activePropertyObject && (
                    <div className="fixed inset-0 z-[10100] flex justify-end">
                        {/* Backdrop */}
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setActivePropertyObjectKey(null)}
                            className="absolute inset-0 bg-black/50 backdrop-blur-xs"
                        />

                        {/* Slide-Out Drawer */}
                        <motion.div 
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="relative w-full max-w-2xl bg-[var(--bg-surface)] h-full shadow-2xl flex flex-col z-10 border-l border-[var(--border)] font-sans"
                        >
                            {/* Drawer Header */}
                            <div className="p-6 border-b border-[var(--border)] flex items-center justify-between bg-[var(--bg-surface)]">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-[var(--bg-muted)] border border-[var(--border)] flex items-center justify-center text-[var(--text-primary)]">
                                        <ObliqueIcon name="lock" size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-[var(--text-primary)] flex items-center gap-2">
                                            Field-Level Security: {activePropertyObject.title}
                                        </h3>
                                        <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                                            Control read-write, read-only, and restricted/hidden property visibility.
                                        </p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setActivePropertyObjectKey(null)}
                                    className="w-8 h-8 rounded-full border-0 bg-[var(--bg-muted)] hover:bg-[var(--bg-subtle)] text-[var(--text-secondary)] flex items-center justify-center cursor-pointer transition-colors"
                                >
                                    <ObliqueIcon name="close" size={18} />
                                </button>
                            </div>

                            {/* Property Search & Legend */}
                            <div className="p-5 border-b border-[var(--border)] bg-[var(--bg-subtle)]/50 flex flex-col gap-3">
                                <div className="flex items-center gap-2 bg-[var(--bg-surface)] border border-[var(--border)] rounded px-3 py-2">
                                    <ObliqueIcon name="search" size={15} color="var(--text-tertiary)" />
                                    <input 
                                        placeholder={`Search ${activePropertyObject.title} properties by name or key...`}
                                        value={propertySearchQuery}
                                        onChange={(e) => setPropertySearchQuery(e.target.value)}
                                        className="border-none bg-transparent outline-none text-xs w-full text-[var(--text-primary)]"
                                    />
                                    {propertySearchQuery && (
                                        <button onClick={() => setPropertySearchQuery('')} className="bg-transparent border-0 text-slate-400 cursor-pointer">
                                            <ObliqueIcon name="close" size={14} />
                                        </button>
                                    )}
                                </div>

                                <div className="flex items-center justify-between text-[11px] text-[var(--text-secondary)]">
                                    <span className="font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">Access Levels:</span>
                                    <div className="flex items-center gap-3">
                                        <span className="flex items-center gap-1 font-medium"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> <strong>Edit</strong> (Read-Write)</span>
                                        <span className="flex items-center gap-1 font-medium"><span className="w-2 h-2 rounded-full bg-amber-500"></span> <strong>View Only</strong> (Locked)</span>
                                        <span className="flex items-center gap-1 font-medium"><span className="w-2 h-2 rounded-full bg-red-500"></span> <strong>Restricted</strong> (Hidden)</span>
                                    </div>
                                </div>
                            </div>

                            {/* Grouped Properties List */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                                {Object.entries(groupedDrawerProperties).map(([groupName, props]) => (
                                    <div key={groupName} className="space-y-3">
                                        <div className="text-xs font-bold text-[var(--text-tertiary)] uppercase tracking-wider flex items-center gap-2">
                                            <span>{groupName}</span>
                                            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-[var(--bg-muted)] text-[var(--text-secondary)] font-normal">{props.length}</span>
                                        </div>

                                        <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl divide-y divide-[var(--border)] overflow-hidden">
                                            {props.map(prop => {
                                                const currentAccess = propertyPermissions[activePropertyObjectKey]?.[prop.key] || 'edit';
                                                
                                                return (
                                                    <div key={prop.key} className="p-4 flex items-center justify-between hover:bg-[var(--bg-subtle)]/30 transition-colors">
                                                        <div className="pr-4 max-w-sm">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs font-bold text-[var(--text-primary)]">{prop.label}</span>
                                                                <code className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-muted)] text-[var(--text-tertiary)] font-mono">
                                                                    {prop.key}
                                                                </code>
                                                            </div>
                                                            <p className="text-[11px] text-[var(--text-secondary)] mt-0.5 leading-snug">
                                                                {prop.desc}
                                                            </p>
                                                        </div>

                                                        {/* 3-Way Segmented Control */}
                                                        <div className="flex items-center bg-[var(--bg-muted)] p-1 rounded-lg border border-[var(--border)] shrink-0">
                                                            <button
                                                                type="button"
                                                                onClick={() => handlePropertyAccessChange(activePropertyObjectKey, prop.key, 'edit')}
                                                                className={`px-2.5 py-1 rounded text-[11px] font-bold cursor-pointer transition-all ${
                                                                    currentAccess === 'edit'
                                                                        ? 'bg-white text-emerald-700 shadow-xs'
                                                                        : 'bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                                                                }`}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handlePropertyAccessChange(activePropertyObjectKey, prop.key, 'view')}
                                                                className={`px-2.5 py-1 rounded text-[11px] font-bold cursor-pointer transition-all ${
                                                                    currentAccess === 'view'
                                                                        ? 'bg-white text-amber-700 shadow-xs'
                                                                        : 'bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                                                                }`}
                                                            >
                                                                View Only
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handlePropertyAccessChange(activePropertyObjectKey, prop.key, 'restricted')}
                                                                className={`px-2.5 py-1 rounded text-[11px] font-bold cursor-pointer transition-all ${
                                                                    currentAccess === 'restricted'
                                                                        ? 'bg-red-500 text-white shadow-xs'
                                                                        : 'bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                                                                }`}
                                                            >
                                                                Restricted
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Drawer Footer */}
                            <div className="p-4 border-t border-[var(--border)] bg-[var(--bg-surface)] flex items-center justify-between">
                                <span className="text-xs text-[var(--text-tertiary)]">
                                    Configured properties will apply to all views & sidebars.
                                </span>
                                <button 
                                    onClick={() => setActivePropertyObjectKey(null)}
                                    className="px-4 py-1.5 rounded bg-[var(--text-primary)] text-[var(--bg-page)] text-xs font-bold cursor-pointer hover:opacity-90 transition-opacity"
                                >
                                    Done
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );

    return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
}
